import { resolveAssetUrl } from "@/components/ui";
import { useBooth } from "../BoothContext";

/** "Vibes" — the AI backdrop the guest picks. */
export function VibeScreen() {
  const booth = useBooth();
  const scenes = booth.config.scenes;

  return (
    <div className="flex h-full flex-col items-center gap-6 overflow-y-auto px-5 pb-8 pt-16">
      <header className="shrink-0 text-center">
        <h2
          className="text-3xl font-bold uppercase leading-tight tracking-tight sm:text-4xl"
          style={{ color: "var(--booth-headline)" }}
        >
          {booth.say("scene_prompt", "Choose your vibe")}
        </h2>
        <p className="mt-2 text-base opacity-70" style={{ color: "var(--booth-subtitle)" }}>
          {booth.say("scene_hint", "Select a scene theme for your photo")}
        </p>
      </header>

      {scenes.length === 0 ? (
        <p className="text-center opacity-70" style={{ color: "var(--booth-subtitle)" }}>
          No {booth.say("scene_plural", "vibes").toLowerCase()} have been configured for this booth yet.
        </p>
      ) : (
        // Two across even on the narrowest kiosk.
        //
        // This was one column on a 390px screen with a 4:5 card, so a single
        // vibe filled the whole display and a guest had to scroll to discover
        // there were others. They choose from thumbnails, in a queue, in a few
        // seconds — comparing them side by side is the entire job of this
        // screen. Three across once there's room.
        <div className="grid w-full max-w-4xl grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {scenes.map((scene) => {
            const image = resolveAssetUrl(scene.image_path);
            return (
              <button
                key={scene.slug}
                onClick={() => booth.selectScene(scene)}
                className="group relative aspect-[3/4] overflow-hidden rounded-2xl border border-booth-line bg-booth-surface text-left shadow-lg transition-transform active:scale-[0.98]"
              >
                {image ? (
                  // A neutral backdrop behind the thumbnail, because operators
                  // upload PNGs with transparent backgrounds and the page colour
                  // showing through reads as a rendering fault rather than a
                  // photo. Only visible where the image actually is transparent.
                  <span className="block h-full w-full bg-[#15171a]">
                    <img src={image} alt="" className="h-full w-full object-cover" />
                  </span>
                ) : (
                  <div className="grid h-full place-items-center text-5xl font-bold text-booth-ink-muted opacity-50">
                    {scene.name.slice(0, 1)}
                  </div>
                )}
                {/* White on a black gradient, deliberately, and NOT booth tokens:
                    this caption sits on the guest's chosen photo, not on the
                    page, so it has to stay readable over any image. */}
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent p-3 pt-8">
                  <span className="block text-base font-bold leading-tight text-white sm:text-lg">
                    {scene.name}
                  </span>
                  {scene.description && (
                    // Clamped: a description is a hint, and an operator's long
                    // one shouldn't push the name off a small card.
                    <span className="mt-0.5 line-clamp-2 block text-2xs leading-snug text-white/70 sm:text-xs">
                      {scene.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
